import React from 'react'
import qs from 'query-string'

import { format, addDays, endOfDay, startOfDay, isAfter } from 'date-fns'
import { Alert, Tabs, Button, Row, Col, Table } from 'antd'
import { ArrowRightOutlined, PlusOutlined } from '@ant-design/icons'
import { FaChevronRight } from 'react-icons/fa'
import { generatePath, useHistory, Link } from 'react-router-dom'
import PageWithActionBar from 'components/PageWithActionBar'
import { useQuery } from '@apollo/react-hooks'

import { employeeAppointmentsQuery } from '../queries'
import Loader from 'components/Loader'
import { sourceColor } from 'modules/calendar/components/TimelineView/Event'
import { VIEW_APPOINTMENT, NEW_APPOINTMENT } from 'modules/appointments/urls'
import { CREATE_CUSTOMER_ROUTE } from 'modules/customers/utils/urls'
import getCustomerInfo from 'modules/customers/utils/getCustomerInfo'

const Card = ({ children, style }) => (
	<div
		style={{
			marginTop: 16,
			padding: 16,
			borderRadius: 10,
			background: '#fff',
			boxShadow: '0px 1px 3px rgba(120,120,120,0.1), 0px 15px 6px -12px rgba(32,32,32,0.03)',
			...style,
		}}
	>
		{children}
	</div>
)

const SideCarStat = ({ value, title }) => {
	return (
		<div>
			<h3 style={{ fontWeight: 500, margin: 0 }}>{value}</h3>
			<p style={{ opacity: 0.7 }}>{title}</p>
		</div>
	)
}

const Overview = ({ user, userId, isMobileDevice }) => {
	const history = useHistory()

	const [state, setState] = React.useState(() => ({
		segment: qs.parse(history.location.search).s || 'today',
	}))

	const { startTime, endTime } = React.useMemo(() => {
		if (state.segment === 'today') {
			return {
				startTime: startOfDay(new Date()),
				endTime: endOfDay(new Date()),
			}
		}

		return {
			startTime: startOfDay(new Date()),
			endTime: addDays(new Date(), 7),
		}
	}, [state.segment])

	const { data, loading } = useQuery(employeeAppointmentsQuery, {
		variables: {
			input: {
				id: userId,
			},
			appointmentsInput: {
				where: {
					status: { notIn: ['deleted', 'canceled'] },
					startTime: {
						gte: startTime,
					},
					endTime: {
						lte: endTime,
					},
				},
			},
		},
	})

	const columns = React.useMemo(
		() => [
			{
				key: 'id',
				title: 'Time',
				render: (appointment) => {
					return (
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<div
								style={{
									backgroundColor: sourceColor[appointment.source.type],
									marginRight: 8,
									left: 4,
									width: 10,
									height: 10,
									borderRadius: '50%',
								}}
							></div>
							{format(appointment.startTime, state.segment !== 'today' ? 'MM/D h:mma' : 'h:mma')}
						</div>
					)
				},
			},
			{
				key: 'services',
				dataIndex: 'services',
				title: 'Service(s)',
				render: (services) => {
					if (services.length === 1) {
						return services[0].name
					}

					if (services.length > 0) {
						return `${services[0].name} + ${services.length - 1} more`
					}

					return <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No Service Info</span>
				},
			},
			{
				key: 'customer',
				title: 'Customer',
				dataIndex: 'customer',
				render: (customer) => {
					if (!customer) {
						return <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No Customer Info</span>
					}

					return `${customer.firstName} ${customer.lastName || ''}`
				},
			},

			{
				key: 'id',
				render: () => {
					return (
						<div style={{ textAlign: 'right' }}>
							<FaChevronRight />
						</div>
					)
				},
			},
		],
		[state.segment]
	)

	const { stats, appointments } = React.useMemo(() => {
		if (!data?.employee) return { stats: {}, appointments: [] }

		const totalBookings = data.employee.appointments.length

		const stats = {
			totalBookings,
			estRevenue: 0,
			avgRevenue: 0,
		}

		const appointments = []
		const now = new Date()

		for (let i = 0; i < totalBookings; i++) {
			const appointment = data.employee.appointments[i]
			stats.estRevenue += appointment.price

			if (!isAfter(appointment.startTime, now)) continue

			appointments.push({
				...appointment,
				customer: getCustomerInfo(appointment.customer),
			})
		}

		stats.avgRevenue = stats.estRevenue > 0 ? (stats.estRevenue / totalBookings).toFixed(2) : 0
		stats.estRevenue = stats.estRevenue.toFixed(2)

		return { stats, appointments }
	}, [data])

	return (
		<PageWithActionBar
			padding={isMobileDevice ? '0px 0px 64px 0px' : '16px'}
			leftContent={<h1>My Dashboard</h1>}
		>
			{user.company.locations.length > 1 && user.company.subscription.plan.accessLevel === 'low' && (
				<Alert
					message="Location Limit Exceeded"
					type="error"
					style={{ marginBottom: '1em' }}
					description={
						<span>
							You've exceeded the total number of allowed locations for your current plan. Please{' '}
							<Link to="/settings/billing">upgrade to a plan</Link> that supports multiple locations
							to prevent losing access and/or data.
						</span>
					}
				/>
			)}

			<div style={{ margin: '0 auto', maxWidth: 1200 }}>
				<div style={{ textAlign: 'right', padding: isMobileDevice ? '16px 16px 0 0' : 0 }}>
					<Link to={CREATE_CUSTOMER_ROUTE}>
						<Button icon={<PlusOutlined />} shape="round" size="large">
							Add Client
						</Button>
					</Link>

					<Link
						style={{ marginLeft: '1em' }}
						to={{
							pathname: NEW_APPOINTMENT,
							state: { employeeId: userId, from: history.location },
						}}
					>
						<Button icon={<PlusOutlined />} type="primary" shape="round" size="large">
							Add Appointment
						</Button>
					</Link>
				</div>

				{loading ? (
					<Loader />
				) : (
					<Row gutter={16}>
						<Col xs={24} lg={18}>
							<Card style={isMobileDevice && { boxShadow: 'none', borderRadius: 0 }}>
								<Tabs
									onChange={(segment) => {
										setState((prev) => ({ ...prev, segment }))
										history.push({
											pathname: history.location.pathname,
											search: `?s=${segment}`,
										})
									}}
									activeKey={state.segment}
								>
									<Tabs.TabPane key="today" tab="Today's Schedule" />
									<Tabs.TabPane key="week" tab="Next 7 Days" />
								</Tabs>

								{appointments.length > 0 ? (
									<Table
										onRow={({ id }) => {
											return {
												style: {
													cursor: 'pointer',
												},
												onClick: () => {
													history.push(generatePath(VIEW_APPOINTMENT, { id }))
												},
											}
										}}
										size="large"
										rowKey="id"
										columns={columns}
										dataSource={appointments}
										pagination={false}
									/>
								) : (
									<h3 style={{ padding: '30px 10px', textAlign: 'center' }}>
										{state.segment === 'today'
											? 'You have no upcoming bookings today.'
											: 'You have no bookings for the next 7 days.'}
									</h3>
								)}
							</Card>
						</Col>
						<Col xs={24} lg={6}>
							<Card style={isMobileDevice && { boxShadow: 'none', borderRadius: 0 }}>
								<h4
									style={{
										fontWeight: 500,
										margin: 0,
									}}
								>
									Estimated Revenue Summary
								</h4>
								<p
									style={{
										paddingBottom: '1em',
										borderBottom: '1px solid rgba(240, 240, 240, 1.0)',
										opacity: 0.7,
									}}
								>
									{state.segment === 'today' ? (
										'Today'
									) : (
										<span>
											{format(new Date(), 'MM/DD/YYYY')} <ArrowRightOutlined />{' '}
											{format(addDays(new Date(), 7), 'MM/DD/YYYY')}{' '}
										</span>
									)}
								</p>
								<SideCarStat value={`$${stats.estRevenue}`} title="Estimated Revenue" />
								<SideCarStat value={stats.totalBookings} title="Bookings" />
								<SideCarStat value={`$${stats.avgRevenue}`} title="Avg Revenue per Booking" />
							</Card>
						</Col>
					</Row>
				)}
			</div>
		</PageWithActionBar>
	)
}

export default Overview
